// components/models/WanFastT2VControlPanel.tsx
import React from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import RangeControl from '../ui/RangeControl';
import Select from '../ui/Select';
import { WanFastT2VParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';

interface WanFastT2VControlPanelProps {
  params: WanFastT2VParams;
  setModelParams: (params: (prevParams: { [model: string]: WanFastT2VParams }) => { [model: string]: WanFastT2VParams }) => void;
  diffusionModel: string;
}

const defaultWanFastT2VParams: WanFastT2VParams = {
  prompt: '',
  negative_prompt: '',
  duration: 5,
  resolution: '480p',
  aspect_ratio: '16:9',
  seed: -1,
};

export const getDefaultWanFastT2VParams = (): WanFastT2VParams => ({ ...defaultWanFastT2VParams });

const RESOLUTION_OPTIONS: NonNullable<WanFastT2VParams['resolution']>[] = ['480p', '580p', '720p'];
// The text-to-video endpoint has no 'auto': anything but 9:16 or 1:1 renders 16:9.
const ASPECT_RATIO_OPTIONS: NonNullable<WanFastT2VParams['aspect_ratio']>[] = ['16:9', '9:16', '1:1'];

const WanFastT2VControlPanel: React.FC<WanFastT2VControlPanelProps> = React.memo(({
  params, setModelParams, diffusionModel,
}) => {
  const { kernel } = useKernel();

  const handleParamChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' || type === 'range' ? parseFloat(value) : value;

    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[WanFastT2VControlPanel] handleParamChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: newValue,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      ...(name === 'prompt' || name === 'negative_prompt'
        ? { value_length: (newValue as string).length }
        : { value: newValue }),
      model_name: diffusionModel,
      source_component: 'WanFastT2VControlPanel',
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[WanFastT2VControlPanel] handleSelectChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'WanFastT2VControlPanel',
    });
  };

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the video you want to generate..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <AutoGrowingTextarea
          label="Negative Prompt"
          placeholder="Properties to avoid."
          name="negative_prompt"
          value={params.negative_prompt ?? ''}
          onChange={handleParamChange}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Duration"
          name="duration"
          min={2}
          max={5}
          step={1}
          value={params.duration}
          onChange={handleParamChange}
          description="In seconds."
        />
      </div>

      <div className="mb-4">
        <Select
          id="wan-2-2-fast-resolution"
          label="Resolution"
          value={params.resolution}
          options={RESOLUTION_OPTIONS}
          onChange={(value) => handleSelectChange('resolution', value)}
        />
      </div>

      <div className="mb-4">
        <Select
          id="wan-2-2-fast-aspect-ratio"
          label="Aspect Ratio"
          value={params.aspect_ratio === '9:16' || params.aspect_ratio === '1:1' ? params.aspect_ratio : '16:9'}
          options={ASPECT_RATIO_OPTIONS}
          onChange={(value) => handleSelectChange('aspect_ratio', value)}
        />
      </div>

      <p className="mb-4 text-xs text-[#777777]">
        Text-to-video: the workspace frame is not used.
      </p>
    </>
  );
});

WanFastT2VControlPanel.displayName = 'WanFastT2VControlPanel';

export default WanFastT2VControlPanel;
